import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Users, MessageSquare, Megaphone, LayoutDashboard, ImageIcon, UserPlus,
  ShieldCheck, AlertTriangle, Loader2,
} from "lucide-react";

/**
 * Franchise Overview — city-scoped equivalent of the Admin Overview.
 * Landing screen after a Franchise operator or their sub-admin logs in.
 *
 * The activity feed below lists BOTH the franchise owner's actions
 * AND their sub-admins' (backend filters by `actor_city`), so the
 * owner has a single "who did what" pane per Section A / Part 9.
 */
export default function FranchiseOverviewPage() {
  const [ov, setOv] = useState(null);
  const [selfCheck, setSelfCheck] = useState(null);
  const [scLoading, setScLoading] = useState(false);
  useEffect(() => { api.franchiseOverview().then((r) => setOv(r.data)).catch(() => setOv({})); }, []);

  const runSelfCheck = async () => {
    if (!ov) return;
    setScLoading(true);
    try {
      const params = new URLSearchParams({
        ui_vendor_count: String(ov.vendor_count ?? 0),
        ui_campaign_count: String(ov.campaign_count ?? 0),
        ui_lead_count: String(ov.lead_count ?? 0),
        ui_enquiry_count: String(ov.pending_enquiries ?? 0),
      });
      const r = await api.franchiseSelfCheck(params.toString());
      setSelfCheck(r.data);
    } catch (e) {
      setSelfCheck({ error: e?.response?.data?.detail || e.message || "Self-check failed" });
    } finally {
      setScLoading(false);
    }
  };
  if (!ov) return <div className="p-6"><Skeleton className="h-40" /></div>;
  const city = ov.assigned_city || "All cities";
  const cards = [
    { label: "Vendors", value: ov.vendor_count ?? 0, icon: Users, to: "/franchise/vendors" },
    { label: "Live campaigns", value: ov.campaign_count ?? 0, icon: Megaphone, to: "/franchise/campaigns" },
    { label: "Pending enquiries", value: ov.pending_enquiries ?? 0, icon: MessageSquare, to: "/franchise/enquiries" },
    { label: "Leads captured", value: ov.lead_count ?? 0, icon: LayoutDashboard, to: "/franchise/leads" },
  ];
  return (
    <div className="space-y-6" data-testid="franchise-overview">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Franchise Dashboard</p>
        <h1 className="text-2xl md:text-3xl font-heading font-semibold">{city}</h1>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} className="block" data-testid={`franchise-card-${c.label.toLowerCase().split(' ')[0]}`}>
            <Card className="p-4 hover:border-primary transition">
              <div className="flex items-center gap-2 text-muted-foreground"><c.icon className="h-4 w-4" /><span className="text-xs uppercase tracking-wider">{c.label}</span></div>
              <div className="mt-2 text-3xl font-bold tabular-nums">{c.value}</div>
            </Card>
          </Link>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
        <Link to="/franchise/homepage-editor" className="block">
          <Card className="p-4 hover:border-primary transition h-full">
            <div className="flex items-center gap-2"><ImageIcon className="h-4 w-4 text-primary" /><span className="font-medium">City Homepage content</span></div>
            <p className="text-xs text-muted-foreground mt-1">Curate cover banner + featured offers + vendor logos for this city.</p>
          </Card>
        </Link>
        <Link to="/franchise/staff" className="block">
          <Card className="p-4 hover:border-primary transition h-full">
            <div className="flex items-center gap-2"><UserPlus className="h-4 w-4 text-primary" /><span className="font-medium">Sub-admin accounts</span></div>
            <p className="text-xs text-muted-foreground mt-1">Delegate actions to your team. Same city, no further nesting.</p>
          </Card>
        </Link>
        <Link to="/franchise/activity" className="block">
          <Card className="p-4 hover:border-primary transition h-full">
            <div className="flex items-center gap-2"><LayoutDashboard className="h-4 w-4 text-primary" /><span className="font-medium">Activity log</span></div>
            <p className="text-xs text-muted-foreground mt-1">Every action taken by you + your sub-admins, chronologically.</p>
          </Card>
        </Link>
      </div>
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Recent activity</h2>
          <Link to="/franchise/activity" className="text-xs text-primary hover:underline">See all</Link>
        </div>
        <div className="mt-3 space-y-2">
          {(ov.recent_activity || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No activity yet.</p>
          ) : ov.recent_activity.slice(0, 10).map((row) => (
            <div key={row.id} className="flex items-start justify-between gap-3 border-b border-border pb-2 last:border-0 last:pb-0">
              <div className="min-w-0">
                <p className="text-sm"><span className="font-medium">{row.actor_email}</span> <Badge variant="secondary" className="ml-1 text-[10px]">{row.actor_role}</Badge></p>
                <p className="text-xs text-muted-foreground truncate">{row.action} · {row.meta && JSON.stringify(row.meta)}</p>
              </div>
              <span className="text-[11px] text-muted-foreground whitespace-nowrap">{new Date(row.at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Phase 20 (B4) — city_id migration self-check.
          Compares three views: what the UI is showing, what the DB
          returns using the old string-city scope, and what the DB
          returns using the new canonical city_id scope. Any drift is
          highlighted so the operator can flag migration gaps. */}
      <Card className="p-4" data-testid="franchise-self-check-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <h2 className="font-semibold">Self-check &amp; migration status</h2>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={runSelfCheck}
            disabled={scLoading}
            data-testid="franchise-self-check-btn"
          >
            {scLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
            {selfCheck ? "Re-run" : "Run self-check"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Compares the numbers on this dashboard against the database, using both the
          legacy string-city scope and the new <span className="font-mono">city_id</span> scope.
          Any mismatch points to records still awaiting the Phase&nbsp;20 backfill.
        </p>
        {selfCheck && selfCheck.error && (
          <p className="mt-3 text-sm text-red-600" data-testid="franchise-self-check-error">
            {String(selfCheck.error)}
          </p>
        )}
        {selfCheck && !selfCheck.error && (
          <div className="mt-4 space-y-4">
            <div className="text-xs text-muted-foreground">
              City: <b>{selfCheck.assigned_city || "(all)"}</b>
              {" · "}
              city_id: <span className="font-mono">{selfCheck.assigned_city_id || "—"}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="franchise-self-check-table">
                <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">Entity</th>
                    <th className="px-3 py-2 text-right">UI</th>
                    <th className="px-3 py-2 text-right">DB (string city)</th>
                    <th className="px-3 py-2 text-right">DB (city_id)</th>
                    <th className="px-3 py-2 text-right">UI drift</th>
                    <th className="px-3 py-2 text-right">Scope drift</th>
                    <th className="px-3 py-2 text-center">OK</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(selfCheck.checks || []).map((row) => (
                    <tr key={row.label}>
                      <td className="px-3 py-2 font-medium">{row.label}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{row.ui ?? "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{row.db_by_string}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{row.db_by_city_id}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${row.ui_drift ? "text-red-600 font-bold" : "text-muted-foreground"}`}>
                        {row.ui_drift ?? "—"}
                      </td>
                      <td className={`px-3 py-2 text-right tabular-nums ${row.scope_drift ? "text-amber-600 font-bold" : "text-muted-foreground"}`}>
                        {row.scope_drift ?? "0"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {row.ok
                          ? <ShieldCheck className="w-4 h-4 text-emerald-600 inline" />
                          : <AlertTriangle className="w-4 h-4 text-amber-600 inline" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
              {[
                {
                  label: "Vendors",
                  ready: selfCheck.coverage?.vendors_with_city_id,
                  total: selfCheck.coverage?.vendors_total,
                },
                {
                  label: "Campaigns",
                  ready: selfCheck.coverage?.campaigns_with_city_id,
                  total: selfCheck.coverage?.campaigns_total,
                },
                {
                  label: "Franchise operators + staff",
                  ready: selfCheck.coverage?.franchise_users_with_city_id,
                  total: selfCheck.coverage?.franchise_users_total,
                },
              ].map((c) => {
                const pct = c.total ? Math.round((c.ready / c.total) * 100) : 0;
                return (
                  <div key={c.label} className="rounded border p-2">
                    <div className="text-muted-foreground">{c.label}</div>
                    <div className="tabular-nums">
                      <b>{c.ready ?? 0}</b> / {c.total ?? 0} ready ({pct}%)
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
